// Theme + per-tenant branding. Both are FIRST-CLASS (spec): data-theme lives on <html>.
//
// Resolution order (explicit and deliberate):
//   1. explicit user choice, stored SERVER-SIDE on the user record (follows them across
//      devices — NOT localStorage, which desyncs across the two-browser sessions people
//      actually run, the same instinct that put Hive's JWTs somewhere they shouldn't be)
//   2. prefers-color-scheme
//   3. dark
//
// The server-side preference arrives in the bootstrap payload the SPA reads on load (here
// modelled by `serverPreference`). We still mirror the *resolved* theme onto <html> so CSS
// applies; we never treat localStorage as the source of truth.

export type Theme = "light" | "dark";
export type ThemePreference = Theme | "system";

import type { TenantBranding } from "./types";

export function resolveTheme(serverPreference: ThemePreference | null): Theme {
  if (serverPreference === "light" || serverPreference === "dark") return serverPreference;
  // 2) prefers-color-scheme
  if (typeof window !== "undefined" && window.matchMedia) {
    if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  }
  // 3) default dark
  return "dark";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

// ── Per-tenant branding (runtime, from tenants.branding) ─────────────────────
// ONLY --gold, --gradient-gold and the logo are overridable. Hive brown, surface warmth, the
// honeycomb texture and the glass system are Action Center's identity and are NEVER touched.
//
// Light-mode gold guard: #E8A317 on cream is ~2.1:1 and fails text contrast, so light mode uses
// a darker gold for TEXT. A tenant brand colour used as a text colour on a light surface must be
// held to the same bar. We apply the tenant's brand gold to fills (buttons, gradient bars, the
// hex mark) always, but in light mode we do NOT let a too-light brand colour become small-text
// gold — we fall back to the token's --gold for text if the brand colour is too light.
export function applyBranding(branding: TenantBranding | null, theme: Theme): void {
  const root = document.documentElement;
  // Reset any previous tenant overrides first (idempotent across tenant switches in one build).
  root.style.removeProperty("--gold");
  root.style.removeProperty("--gradient-gold");

  if (!branding) return;

  if (branding.gradient_gold) {
    root.style.setProperty("--gradient-gold", branding.gradient_gold);
  }
  if (branding.gold) {
    // Fills always get the brand gold.
    if (branding.gradient_gold === undefined) {
      root.style.setProperty("--gradient-gold", `linear-gradient(135deg, ${branding.gold}, ${branding.gold})`);
    }
    // Text gold: in light mode, only accept the brand colour if it clears the contrast bar on
    // the cream canvas; otherwise keep the accessible token gold for text.
    if (theme === "dark" || contrastRatio(branding.gold, "#F7F1E6") >= 4.5) {
      root.style.setProperty("--gold", branding.gold);
    }
    // else: leave --gold at the theme default so small gold text stays AA-compliant.
  }
}

// WCAG relative-luminance contrast ratio between two hex colours.
export function contrastRatio(hexA: string, hexB: string): number {
  const l1 = luminance(hexA);
  const l2 = luminance(hexB);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
